import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  compose,
  formatDate,
  formatNumbers,
} from '../../../services/utilityFunctions';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import ServerSideList from '../../../commonComponents/lists/ServerSideList';
import translate from '../../../commonComponents/translate';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import SponsorUserChangeModal from '../../../commonComponents/modal/SponsorUserChangeModal';
import { useModal } from '../../../services/customHooks';
import withCSRF from '../../../commonComponents/withCSRF';

const displayVA = value => (typeof value === 'number' ? value : '-');
const displayCommission = value => {
  if (typeof value !== 'number') {
    return '-';
  }
  return value ? `${formatNumbers(value, false, true)}€` : `${0}€`;
};

function ActionsColumn({ t, id, sponsoredBy, loggedUserId }) {
  return (
    <>
      {loggedUserId === sponsoredBy ? (
        <div>
          <span className="dropdown">
            <a
              href="#"
              className="btn m-btn m-btn--hover-brand m-btn--icon m-btn--icon-only m-btn--pill"
              data-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="la la-ellipsis-h" />
            </a>
            <div
              className="dropdown-menu dropdown-menu-right"
              x-placement="bottom-end"
            >
              <a
                className="dropdown-item change-sponsor-user"
                href="#"
                data-user-id={id}
                data-sponsor-id={sponsoredBy}
              >
                <i className="la la-group" />
                {t('modal.change_sponsor_user')}
              </a>
            </div>
          </span>
        </div>
      ) : null}
    </>
  );
}

ActionsColumn.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  sponsoredBy: PropTypes.string.isRequired,
  loggedUserId: PropTypes.string.isRequired,
};

function NetworkListTable(props) {
  const { t, filters } = props;
  const { loggedUser } = useContext(LoggedUserContext);

  const configs = useContext(ConfigsContext);
  const { VAIntegration, binaryIntegration, salesIntegration } =
    configs?.tenant?.settings?.directSellingSettings || {};
  const { settings: organizationSettings } = configs?.organization;

  /** region List Props */
  const tableRef = useRef();
  const tableId = 'networkList';
  const tableEndpoint = '/table/user/network/list';
  const tableHeaders = {
    'Accept-Version': '1.0.0',
    'X-Gamification-API': 'Organization',
  };
  const tableParams = { populate: ['sponsoredBy', 'recruitedBy'] };
  /** endregion */

  // region Modal: Change Sponsor
  const [showChangeSponsorUserModal, setShowChangeSponsorUserModal] = useModal({
    show: false,
  });

  const handleChangeSponsorUser = useCallback(user => {
    if (user && tableRef.current) {
      tableRef.current.forceReload();
    }
    setShowChangeSponsorUserModal({ show: false });
  });
  // endregion Modal: Change Sponsor

  function handleListInitialization() {
    $(`#${tableId}`).on(
      'click',
      '.change-sponsor-user',
      function changeHandler() {
        const userId = $(this).attr('data-user-id');
        const sponsoredBy = $(this).attr('data-sponsor-id');
        setShowChangeSponsorUserModal({
          show: true,
          data: { userId, sponsoredBy },
        });
      },
    );
  }

  const tableColumns = useMemo(() => {
    const columns = [
      {
        field: 'firstName',
        title: t('name'),
        textAlign: 'left',
        width: 150,
        template: user =>
          renderToStaticMarkup(
            <UserColumn
              t={t}
              user={user}
              includeWrapper
              includeInvitedStatus
              invitedText={t('invited')}
            />,
          ),
      },
      {
        field: 'role',
        title: t('role'),
        textAlign: 'left',
        width: 100,
        sortable: false,
        template: ({ type }) =>
          renderToStaticMarkup(<>{t(`user_type_${type}`)}</>),
      },
      {
        field: 'sponsoredBy',
        title: t('sponsor'),
        textAlign: 'left',
        width: 150,
        template: ({ sponsoredBy }) =>
          renderToStaticMarkup(
            <UserColumn t={t} user={sponsoredBy} includeWrapper />,
          ),
      },
      {
        field: 'recruitedBy',
        title: t('recruiter'),
        textAlign: 'left',
        width: 150,
        template: ({ recruitedBy }) =>
          renderToStaticMarkup(
            recruitedBy ? (
              <UserColumn t={t} user={recruitedBy} includeWrapper />
            ) : (
              '-'
            ),
          ),
      },
      {
        field: 'level',
        title: t('level'),
        textAlign: 'left',
        width: 75,
        sortable: false,
        template: ({ skillLevel }) =>
          renderToStaticMarkup(<>{skillLevel?.name || '-'}</>),
      },
      {
        field: 'forecastLevel',
        title: t('teamStructure.card.level.forecast'),
        textAlign: 'left',
        width: 75,
        sortable: false,
        template: ({ forecast }) =>
          renderToStaticMarkup(<>{forecast?.skillLevel?.name || '-'}</>),
      },
      {
        field: 'createdAt',
        title: t('member_since'),
        textAlign: 'center',
        width: 90,
        sortable: 'desc',
        template: ({ createdAt }) =>
          renderToStaticMarkup(<>{formatDate(createdAt, 'DD MMM YYYY')}</>),
      },
    ];

    if (VAIntegration && !binaryIntegration) {
      columns.push(
        {
          field: 'VA',
          title: t('commissions.personalVA'),
          width: 65,
          textAlign: 'center',
          sortable: false,
          template: ({ personalVA }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayVA(personalVA)}
              </span>,
            ),
        },
        {
          field: 'groupVA',
          title: t('commissions.groupVA'),
          width: 65,
          textAlign: 'center',
          sortable: false,
          template: ({ groupVA }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayVA(groupVA)}
              </span>,
            ),
        },
      );
    }

    if (VAIntegration && binaryIntegration) {
      columns.push(
        {
          field: 'myVA',
          title: t('commissions.myVA'),
          width: 50,
          textAlign: 'center',
          sortable: false,
          template: ({ myVA }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">{displayVA(myVA)}</span>,
            ),
        },
        {
          field: 'VALeft',
          title: t('commissions.vaLeft'),
          width: 60,
          textAlign: 'center',
          sortable: false,
          template: ({ VALeft }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayVA(VALeft)}
              </span>,
            ),
        },
        {
          field: 'VARight',
          title: t('commissions.vaRight'),
          width: 60,
          textAlign: 'center',
          sortable: false,
          template: ({ VARight }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayVA(VARight)}
              </span>,
            ),
        },
      );
    }

    if (salesIntegration) {
      columns.push(
        {
          field: 'personalCommissions',
          title: t('commission.modal.sale.commission'),
          width: 80,
          textAlign: 'center',
          sortable: false,
          template: ({ forecast }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayCommission(forecast?.personalCommissions)}
              </span>,
            ),
        },
        {
          field: 'groupCommissions',
          title: t('group.commission.abbreviation'),
          width: 60,
          textAlign: 'center',
          sortable: false,
          template: ({ forecast }) =>
            renderToStaticMarkup(
              <span className="color-dynamic bold-500">
                {displayCommission(forecast?.groupCommissions)}
              </span>,
            ),
        },
      );
    }

    if (organizationSettings?.allowUsersToChangeSponsor) {
      columns.push({
        field: 'actions',
        width: 40,
        textAlign: 'center',
        title: '',
        sortable: false,
        overflow: 'visible',
        template: ({ _id, sponsoredBy }) =>
          renderToStaticMarkup(
            <ActionsColumn
              t={t}
              id={_id}
              sponsoredBy={
                sponsoredBy && sponsoredBy._id ? sponsoredBy._id : ''
              }
              loggedUserId={loggedUser._id}
            />,
          ),
      });
    }
    return columns;
  }, [
    VAIntegration,
    salesIntegration,
    binaryIntegration,
    displayCommission,
    displayVA,
    organizationSettings.allowUsersToChangeSponsor,
  ]);

  return (
    <div className="networkListTableContainer">
      {showChangeSponsorUserModal.show && (
        <SponsorUserChangeModal
          userTypeNames={{}}
          userId={showChangeSponsorUserModal.data.userId}
          sponsoredBy={showChangeSponsorUserModal.data.sponsoredBy}
          onClose={handleChangeSponsorUser}
        />
      )}
      <ServerSideList
        ref={tableRef}
        columns={tableColumns}
        id={tableId}
        dataSourceURL={tableEndpoint}
        dataSourceHeaders={tableHeaders}
        dataSourceParams={tableParams}
        onInit={handleListInitialization}
        query={{
          ...filters,
          showInvited: filters.showInvited ? filters.showInvited : undefined,
        }}
      />
    </div>
  );
}

NetworkListTable.propTypes = {
  t: PropTypes.func.isRequired,
  filters: {
    search: PropTypes.string,
    sponsoredBy: PropTypes.string,
    recruitedBy: PropTypes.string,
    level: PropTypes.string,
    status: PropTypes.string,
    user: PropTypes.string,
  },
};

NetworkListTable.defaultProps = {
  filters: {
    search: null,
    sponsoredBy: null,
    recruitedBy: null,
    level: null,
    status: null,
    user: null,
  },
};

export default compose(translate, withCSRF)(NetworkListTable);
